import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Radio } from 'src/core/forms';
import { RadioGroup } from 'src/core/forms';
import classnames from 'classnames';
import { swatchCss, SwatchKind } from 'src/components/ProductDetail/utils/swatchData';
import { capitalizeFirstLetter } from 'src/util/text';
import { useFormApi } from 'src/core/forms';
import { PickerLabel } from 'src/components/PickerLabel/PickerLabel';
import { RxFormContext } from 'src/core/forms';
import { useDeps } from 'src/hooks/useDeps';
import { useDispatch } from 'react-redux';
import { auditTime, distinctUntilChanged, filter, map } from 'rxjs';

import { Selector } from '../Selector/Selector';
import { SwatchOption } from '../Swatch/SwatchOption';

import { Swatch } from './ProductSwatch';
import classes from './ProductSwatch.scss';

type Props = {
    title?: string;
    swatches: Swatch[];
    enableDropdownView: boolean;
    resetShippingGroups?: () => void;
};

const COLOR_FIELD_NAME = 'color';

const formatTitle = (title: string) => {
    const splitTitle = title.split(' ');

    return splitTitle
        .map(
            (titleElement) =>
                titleElement.slice(0, 1).toUpperCase() + titleElement.slice(1, titleElement.length).toLowerCase(),
        )
        .join(' ');
};

export const colourSelectItems = (
    swatches: Swatch[],
    initialColorState: number,
    handleChangeColorState: (value: number) => void,
) => {
    return swatches.map((swatch, index) => (
        <SwatchOption
            htmlFor={`swatch-${swatch.value_index}`}
            key={index}
            optionName={formatTitle(swatch.title)}
            labelClassName={classnames(classes.swatchesItemLabelMobile, {
                [classes.swatchesItemLabelMobileActive]: swatch.value_index === initialColorState,
            })}
            onClick={() => handleChangeColorState(swatch.value_index)}
            input={
                <input
                    name={COLOR_FIELD_NAME}
                    className={classes.swatchesItemInput}
                    value={swatch.value_index}
                    id={`swatch-${swatch.value_index}`}
                    checked={swatch.value_index === initialColorState}
                    type="radio"
                />
            }
            visibleLabel={<span>{formatTitle(swatch.title)}</span>}
        >
            <span className={classes.swatchesItem} style={swatchCss(swatch.swatch_data)} data-value={swatch.title}>
                <img src={require('../../../static/icons/check.png')} alt="" data-img="img-only" />
            </span>
        </SwatchOption>
    ));
};

export const dropdownSwatchesList = (swatches: Swatch[]) =>
    swatches.map((swatch) => ({
        name: capitalizeFirstLetter(swatch.title.toLowerCase()),
        value: String(swatch.value_index),
        image: swatch.swatch_data.kind === SwatchKind.ImageSwatchData ? swatch.swatch_data.value : undefined,
        swatch: swatch.swatch_data.kind === SwatchKind.ColorSwatchData ? swatch.swatch_data.value : undefined,
    }));

export const ColourPicker: React.FunctionComponent<Props> = ({
    swatches,
    title,
    enableDropdownView,
    resetShippingGroups,
}) => {
    const [isDropdownView, setIsDropdownView] = useState<boolean>(enableDropdownView);

    useEffect(() => {
        setIsDropdownView(enableDropdownView);
    }, [enableDropdownView]);

    const formApi = useFormApi();
    const { getValueStream } = useContext(RxFormContext);
    const dispatch = useDispatch();
    const { replace } = useDeps();
    const initialColorState = formApi.getValue(COLOR_FIELD_NAME);

    useEffect(() => {
        const sub = getValueStream()
            .pipe(
                map((s) => s?.[COLOR_FIELD_NAME]),
                filter((v) => v !== undefined && v !== null && v !== ''),
                map((v: number) => String(v)),
                distinctUntilChanged(),
                auditTime(150),
            )
            .subscribe((value) => {
                const url = new URL(window.location.href);
                const prev = url.searchParams.get(COLOR_FIELD_NAME);

                if (prev === value) return;

                url.searchParams.set(COLOR_FIELD_NAME, value);

                const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                const next = `${url.pathname}${url.search}${url.hash}`;
                if (next === current) return;

                requestAnimationFrame(() => {
                    dispatch(replace(next));
                });
            });

        return () => sub.unsubscribe();
    }, [dispatch, getValueStream, replace]);

    const handleChangeColorState = (value) => {
        formApi.setValue(COLOR_FIELD_NAME, value);
        formApi.setValues({ [COLOR_FIELD_NAME]: value });
        resetShippingGroups && requestAnimationFrame(() => resetShippingGroups());
    };

    const delayedResetShipping = useCallback(() => {
        resetShippingGroups && requestAnimationFrame(() => resetShippingGroups());
    }, [resetShippingGroups]);

    const colourSwatches = swatches.map((swatch) => (
        <SwatchOption
            key={swatch.title}
            optionName={swatch.title}
            labelClassName={classnames(classes.swatchesItemLabel, classes.pickerLabel)}
            input={
                <Radio
                    value={swatch.value_index}
                    id={`swatch-${swatch.value_index}`}
                    data-value={swatch.title}
                    className={classes.swatchesItemInput}
                />
            }
        >
            <span className={classes.swatchesItem} style={swatchCss(swatch.swatch_data)} data-value={swatch.title} />
        </SwatchOption>
    ));

    const colourSelectItemsComponent = colourSelectItems(swatches, initialColorState, handleChangeColorState);
    const dropdownSwatchesListComponent = dropdownSwatchesList(swatches);

    return (
        <div className={classes.swatches}>
            <div className={classes.pickerHeader}>
                <h2 className={classes.pickerTitle}>{title}</h2>
                <PickerLabel isDropdownView={isDropdownView} setIsDropdownView={setIsDropdownView} title="colour" />
            </div>
            {!isDropdownView ? (
                <div
                    className={classnames(classes.swatchesList, classes.pickerList)}
                    data-test-id="ProductSwatch_SwatchList"
                >
                    <RadioGroup field={COLOR_FIELD_NAME} onChange={delayedResetShipping}>
                        {colourSwatches}
                    </RadioGroup>
                </div>
            ) : (
                <Selector
                    selectItems={colourSelectItemsComponent}
                    selectOptions={dropdownSwatchesListComponent}
                    handleChangeState={handleChangeColorState}
                    initialState={initialColorState}
                    field={COLOR_FIELD_NAME}
                    accessibleName="Choose colour"
                />
            )}
        </div>
    );
};
